import type { CameraTrackSlot } from "../../interface/webrtc_media_slots";
import type { MediaSinkRegistration } from "./external_display_types";
import {
  getCameraFaultSnapshot,
  isCameraFaultBlackActive,
  isCameraFaultChoppyActive,
  subscribeCameraFault,
} from "../../hooks/remoteClient/camera_fault_store";

// 끊김(저fps) 모사 파이프라인: clone → MediaStreamTrackProcessor → (프레임 솎기) →
// MediaStreamTrackGenerator. 버퍼링 없이 즉시 전달/폐기하므로 지연이 추가되지 않는다.
type ChoppyPipeline = {
  generator: MediaStreamTrack;
  abort: AbortController;
};

type RegisteredSink = {
  id: string;
  registration: MediaSinkRegistration;
  activeTrack: MediaStreamTrack | null;
  ownsTrack: boolean;
  // 싱크에 넘긴 MediaStream — 장애 모사 시 srcObject 재할당 없이 이 스트림 안에서 트랙만 교체한다.
  stream: MediaStream | null;
  choppy: ChoppyPipeline | null;
};

type TrackProcessorCtor = new (init: { track: MediaStreamTrack }) => {
  readable: ReadableStream<{ close: () => void }>;
};
type TrackGeneratorCtor = new (init: { kind: "video" }) => MediaStreamTrack & {
  writable: WritableStream<{ close: () => void }>;
};

// 반 프레임(60Hz 기준) 여유 — 30fps 입력에서 목표 간격 경계에 걸린 프레임이 한 칸씩 밀려
// 실제 fps 가 목표보다 낮아지는 것을 막는다.
const CHOPPY_INTERVAL_TOLERANCE_MS = 8;

let choppyUnsupportedWarned = false;

function getTrackProcessorApis(): {
  Processor: TrackProcessorCtor;
  Generator: TrackGeneratorCtor;
} | null {
  const g = globalThis as unknown as {
    MediaStreamTrackProcessor?: TrackProcessorCtor;
    MediaStreamTrackGenerator?: TrackGeneratorCtor;
  };
  if (
    typeof g.MediaStreamTrackProcessor !== "function" ||
    typeof g.MediaStreamTrackGenerator !== "function" ||
    typeof TransformStream !== "function" ||
    typeof AbortController !== "function"
  ) {
    return null;
  }
  return { Processor: g.MediaStreamTrackProcessor, Generator: g.MediaStreamTrackGenerator };
}

function warnChoppyUnsupported(reason: string): void {
  if (choppyUnsupportedWarned) {
    return;
  }
  choppyUnsupportedWarned = true;
  // eslint-disable-next-line no-console
  console.warn(`[CAMERA-FAULT] 끊김(저fps) 모사 미지원 — 무시함: ${reason}`);
}

function setTrackEnabled(track: MediaStreamTrack | null | undefined, enabled: boolean): void {
  if (track && track.kind === "video") {
    track.enabled = enabled;
  }
}

function createSinkId(registration: MediaSinkRegistration): string {
  if (registration.id) {
    return registration.id;
  }

  return `${registration.role}:${registration.slot}:${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}

function stopTrack(track: MediaStreamTrack | null): void {
  if (!track || typeof track.stop !== "function") {
    return;
  }

  try {
    track.stop();
  } catch (_err) {
    // Ignore stop failures from browser-managed remote tracks.
  }
}

function createSinkStream(track: MediaStreamTrack): {
  stream: MediaStream;
  activeTrack: MediaStreamTrack;
  ownsTrack: boolean;
} {
  const ownsTrack = typeof track.clone === "function";
  const sinkTrack = ownsTrack ? track.clone() : track;
  let stream: MediaStream;

  if (typeof MediaStream !== "undefined") {
    try {
      stream = new MediaStream([sinkTrack]);
    } catch (_err) {
      stream = {
        getTracks: () => [sinkTrack],
      } as MediaStream;
    }
  } else {
    stream = {
      getTracks: () => [sinkTrack],
    } as MediaStream;
  }

  return {
    stream,
    activeTrack: sinkTrack,
    ownsTrack,
  };
}

export class CameraMediaSinkRegistry {
  private readonly sinks = new Map<string, RegisteredSink>();

  private readonly currentTracks = new Map<CameraTrackSlot, MediaStreamTrack>();

  constructor() {
    // 영상 장애 모사(개발자 도구) — 레지스트리는 메인(opener) 창에만 존재하므로 여기서 한 번
    // 구독하면 메인 HUD 타일과 보조 팝업 미러 모두에 동일하게 적용된다.
    subscribeCameraFault(() => {
      this.sinks.forEach((entry) => this.applyCameraFault(entry));
    });
  }

  registerSink(registration: MediaSinkRegistration): () => void {
    const id = createSinkId(registration);
    const entry: RegisteredSink = {
      id,
      registration: {
        ...registration,
        id,
      },
      activeTrack: null,
      ownsTrack: false,
      stream: null,
      choppy: null,
    };

    this.sinks.set(id, entry);
    this.attachSlotToSink(entry, this.currentTracks.get(registration.slot) || null);

    return () => {
      this.detachSink(entry);
      this.sinks.delete(id);
    };
  }

  setTrack(slot: CameraTrackSlot, track: MediaStreamTrack | null): void {
    if (track) {
      this.currentTracks.set(slot, track);
    } else {
      this.currentTracks.delete(slot);
    }

    // 이 슬롯을 구독하는 모든 싱크(콕핏 그리드 타일 + 보조 팝업 미러 등)에 fan-out.
    // [DISPLAY] 로그: 슬롯→싱크(역할별) 전달 현황을 남겨, 좌/우 미러 비대칭(우측 검은 화면,
    // #3) 진단을 가능하게 한다. 매칭 싱크가 0개면 "no-sinks" 로 표시.
    let matched = 0;
    this.sinks.forEach((entry) => {
      if (entry.registration.slot !== slot) {
        return;
      }

      matched += 1;
      const delivered = this.attachSlotToSink(entry, track);
      // eslint-disable-next-line no-console
      console.log("[DISPLAY] setTrack slot→sink", {
        slot,
        role: entry.registration.role,
        sinkId: entry.registration.id ?? null,
        hasTrack: Boolean(track),
        delivered,
      });
    });

    if (matched === 0) {
      // eslint-disable-next-line no-console
      console.log("[DISPLAY] setTrack slot→sink", {
        slot,
        role: "no-sinks",
        hasTrack: Boolean(track),
        delivered: false,
      });
    }
  }

  clear(): void {
    this.sinks.forEach((entry) => {
      this.detachSink(entry);
    });
    this.currentTracks.clear();
  }

  // 반환값: 트랙을 실제로 싱크에 attach 했으면 true, (미가용/트랙없음으로) detach 했으면 false.
  private attachSlotToSink(
    entry: RegisteredSink,
    track: MediaStreamTrack | null,
  ): boolean {
    const available =
      typeof entry.registration.isAvailable !== "function" ||
      entry.registration.isAvailable();

    if (!available || !track) {
      this.detachSink(entry);
      return false;
    }

    this.detachSink(entry);

    const { stream, activeTrack, ownsTrack } = createSinkStream(track);
    entry.activeTrack = activeTrack;
    entry.ownsTrack = ownsTrack;
    entry.stream = stream;
    // attach 전에 현재 장애 상태를 반영 — 블랙/끊김 도중 붙은 싱크도 동일하게 보인다.
    this.applyCameraFault(entry);
    entry.registration.attach(stream);
    return true;
  }

  /**
   * 장애 모사 상태를 싱크의 clone 트랙에 반영한다. 원본 트랙(수신 FPS 통계 원천)은 건드리지
   * 않으며, clone 을 소유하지 않은 싱크(clone 미지원 환경)는 건너뛴다.
   */
  private applyCameraFault(entry: RegisteredSink): void {
    const clone = entry.activeTrack;
    if (!entry.ownsTrack || !clone || clone.kind !== "video") {
      return;
    }

    const fault = getCameraFaultSnapshot();
    if (isCameraFaultChoppyActive(fault)) {
      this.startChoppy(entry);
    } else {
      this.stopChoppy(entry);
    }

    // 블랙: enabled=false 인 비디오 트랙은 검은 프레임으로 렌더된다(끊김 파이프라인이 있으면
    // 원천 clone 과 generator 둘 다 끈다).
    const enabled = !isCameraFaultBlackActive(fault);
    setTrackEnabled(clone, enabled);
    setTrackEnabled(entry.choppy?.generator, enabled);
  }

  private startChoppy(entry: RegisteredSink): void {
    if (entry.choppy) {
      return;
    }
    const clone = entry.activeTrack as MediaStreamTrack;
    const stream = entry.stream;
    const apis = getTrackProcessorApis();
    if (!apis) {
      warnChoppyUnsupported("MediaStreamTrackProcessor/Generator 없음");
      return;
    }
    if (!stream || typeof stream.addTrack !== "function" || typeof stream.removeTrack !== "function") {
      warnChoppyUnsupported("MediaStream addTrack/removeTrack 없음");
      return;
    }

    try {
      // 주의: 팝업 싱크도 이 레지스트리(opener 창)에서 generator 트랙을 만든다. 팝업의 <video> 는
      // opener 가 만든 MediaStream 을 그대로 srcObject 로 쓰므로 교체된 트랙도 함께 보인다
      // (실제 Chrome 에서 팝업 렌더 확인 필요).
      const processor = new apis.Processor({ track: clone });
      const generator = new apis.Generator({ kind: "video" });
      const abort = new AbortController();
      let lastForwardedAt = -Infinity;
      const transformer = new TransformStream<{ close: () => void }, { close: () => void }>({
        transform(frame, controller) {
          const fps = getCameraFaultSnapshot().choppyFps;
          const now = performance.now();
          if (now - lastForwardedAt >= 1000 / fps - CHOPPY_INTERVAL_TOLERANCE_MS) {
            lastForwardedAt = now;
            controller.enqueue(frame);
          } else {
            frame.close();
          }
        },
      });
      processor.readable
        .pipeThrough(transformer, { signal: abort.signal })
        .pipeTo(generator.writable, { signal: abort.signal })
        .catch(() => {
          // abort(해제) 또는 트랙 종료로 인한 파이프 중단은 정상 흐름.
        });

      // 같은 MediaStream 객체 안에서 교체(srcObject 재할당 금지 — play() AbortError/검은 화면 경합).
      // 비디오 트랙이 0개인 순간이 없도록 추가 후 제거한다.
      stream.addTrack(generator);
      stream.removeTrack(clone);
      entry.choppy = { generator, abort };
    } catch (err) {
      warnChoppyUnsupported(String(err));
    }
  }

  private stopChoppy(entry: RegisteredSink): void {
    const choppy = entry.choppy;
    if (!choppy) {
      return;
    }
    entry.choppy = null;
    const stream = entry.stream;
    const clone = entry.activeTrack;
    try {
      if (stream && clone && entry.stream?.getTracks().indexOf(clone) === -1) {
        stream.addTrack(clone);
      }
      stream?.removeTrack(choppy.generator);
    } catch (_err) {
      // 스트림이 이미 해제된 경우 무시.
    }
    // abort 는 processor readable 을 cancel 하고(대기 프레임 해제) generator writable 을 중단한다.
    choppy.abort.abort();
    stopTrack(choppy.generator);
  }

  private detachSink(entry: RegisteredSink): void {
    this.stopChoppy(entry);
    entry.stream = null;
    if (entry.ownsTrack) {
      stopTrack(entry.activeTrack);
    }
    entry.activeTrack = null;
    entry.ownsTrack = false;
    entry.registration.detach();
  }
}

export function createCameraMediaSinkRegistry(): CameraMediaSinkRegistry {
  return new CameraMediaSinkRegistry();
}
